import React, { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Chip, Stack, Tooltip } from '@mui/material';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { SET_USER_VIP_STATUS_MUTATION } from '../../gql';
import { AdminUserRow } from './types';
import { defineMessages, useIntl } from 'react-intl';
import { useMaterialReactTableLocalization } from '../../i18n';
import { Switch } from '../../components/design-system';

const gridStyles = { marginBottom: 6, display: 'grid' };
const premiumChipsStyles = { flexDirection: 'row', gap: 0.5 };
const centeredCellProps = { align: 'center' as const };
const messages = defineMessages({
  grantVip: {
    id: 'admin.users.grantVip',
    defaultMessage: 'Grant VIP access',
  },
  revokeVip: {
    id: 'admin.users.revokeVip',
    defaultMessage: 'Revoke VIP access',
  },
  roleAdmin: {
    id: 'admin.users.role.admin',
    defaultMessage: 'Admin',
  },
  roleUser: {
    id: 'admin.users.role.user',
    defaultMessage: 'User',
  },
  yes: { id: 'common.yes', defaultMessage: 'Yes' },
  no: { id: 'common.no', defaultMessage: 'No' },
});

function getDisplayName(user: AdminUserRow): string {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');

  return user.displayName || fullName || '—';
}

interface VipStatusToggleProps {
  user: AdminUserRow;
  onError: (message: string) => void;
}

const VipStatusToggle = React.memo(
  ({ user, onError }: VipStatusToggleProps) => {
    const intl = useIntl();
    const [updating, setUpdating] = useState(false);
    const [setVipStatus] = useMutation(SET_USER_VIP_STATUS_MUTATION);
    const inputProps = useMemo(
      () => ({
        'aria-label': intl.formatMessage({
          id: 'admin.users.vipAccess',
          defaultMessage: 'VIP premium access',
        }),
      }),
      [intl]
    );
    const handleChange = useCallback(
      async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setUpdating(true);

        try {
          await setVipStatus({
            variables: { userId: user.id, enabled: checked },
          });
        } catch (error) {
          onError(
            intl.formatMessage({
              id: 'admin.users.vipUpdateError',
              defaultMessage: 'VIP access could not be updated.',
            })
          );
        } finally {
          setUpdating(false);
        }
      },
      [intl, onError, setVipStatus, user]
    );

    return (
      <Tooltip
        title={intl.formatMessage(
          user.hasVipAccess ? messages.revokeVip : messages.grantVip
        )}
      >
        <span>
          <Switch
            checked={user.hasVipAccess}
            disabled={updating}
            inputProps={inputProps}
            onChange={handleChange}
          />
        </span>
      </Tooltip>
    );
  }
);

interface PremiumStatusProps {
  user: AdminUserRow;
}

const PremiumStatus = React.memo(({ user }: PremiumStatusProps) => {
  const intl = useIntl();

  if (!user.premiumAccess.isPremium) {
    return (
      <Chip
        label={intl.formatMessage({
          id: 'admin.users.premium.free',
          defaultMessage: 'Free',
        })}
        size="small"
        variant="outlined"
      />
    );
  }

  const hasVip = user.premiumAccess.sources.includes('vip');
  const hasPaid = user.premiumAccess.sources.includes('paid_subscription');

  return (
    <Stack sx={premiumChipsStyles}>
      {hasVip ? <Chip color="secondary" label="VIP" size="small" /> : null}
      {hasPaid ? (
        <Chip
          color="primary"
          label={intl.formatMessage({
            id: 'admin.users.premium.paid',
            defaultMessage: 'Paid',
          })}
          size="small"
        />
      ) : null}
    </Stack>
  );
});

interface AdminUsersTableProps {
  rows: AdminUserRow[];
  loading: boolean;
  onError: (message: string) => void;
}

export const AdminUsersTable = React.memo(
  ({ rows, loading, onError }: AdminUsersTableProps) => {
    const intl = useIntl();
    const localization = useMaterialReactTableLocalization();
    const columns = useMemo<MRT_ColumnDef<AdminUserRow>[]>(
      () => [
        {
          id: 'name',
          accessorFn: getDisplayName,
          header: intl.formatMessage({
            id: 'common.name',
            defaultMessage: 'Name',
          }),
          size: 180,
        },
        {
          accessorKey: 'email',
          header: intl.formatMessage({
            id: 'admin.users.email',
            defaultMessage: 'Email',
          }),
          size: 240,
        },
        {
          accessorKey: 'role',
          header: intl.formatMessage({
            id: 'admin.users.role',
            defaultMessage: 'Role',
          }),
          size: 90,
          Cell: ({ cell }) =>
            intl.formatMessage(
              cell.getValue<AdminUserRow['role']>() === 'admin'
                ? messages.roleAdmin
                : messages.roleUser
            ),
        },
        {
          accessorKey: 'verified',
          header: intl.formatMessage({
            id: 'admin.users.verified',
            defaultMessage: 'Verified',
          }),
          size: 90,
          Cell: ({ cell }) =>
            intl.formatMessage(
              cell.getValue<boolean>() ? messages.yes : messages.no
            ),
        },
        {
          accessorKey: 'findCount',
          header: intl.formatMessage({
            id: 'map.tab.finds',
            defaultMessage: 'Finds',
          }),
          size: 80,
        },
        {
          accessorKey: 'siteCount',
          header: intl.formatMessage({
            id: 'map.tab.sites',
            defaultMessage: 'Sites',
          }),
          size: 80,
        },
        {
          accessorKey: 'objectCount',
          header: intl.formatMessage({
            id: 'map.tab.objects',
            defaultMessage: 'Objects',
          }),
          size: 90,
        },
        {
          id: 'premium',
          header: intl.formatMessage({
            id: 'admin.users.premium',
            defaultMessage: 'Premium',
          }),
          size: 130,
          Cell: ({ row }) => <PremiumStatus user={row.original} />,
        },
        {
          accessorFn: (row) => new Date(row.createdAt),
          header: intl.formatMessage({
            id: 'admin.users.registered',
            defaultMessage: 'Registered',
          }),
          size: 130,
          Cell: ({ cell }) =>
            intl.formatDate(cell.getValue<Date>(), {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }),
        },
        {
          id: 'vip',
          header: 'VIP',
          size: 80,
          enableSorting: false,
          muiTableBodyCellProps: centeredCellProps,
          muiTableHeadCellProps: centeredCellProps,
          Cell: ({ row }) => (
            <VipStatusToggle user={row.original} onError={onError} />
          ),
        },
      ],
      [intl, onError]
    );
    const table = useMaterialReactTable({
      columns,
      data: rows,
      localization,
      enableColumnActions: false,
      enableColumnFilters: false,
      enableToolbarInternalActions: false,
      initialState: {
        density: 'compact',
        pagination: { pageIndex: 0, pageSize: 50 },
      },
      muiPaginationProps: { showRowsPerPage: false },
      muiTablePaperProps: { sx: gridStyles },
      state: { isLoading: loading },
    });

    return <MaterialReactTable table={table} />;
  }
);
